/**
 * Ability icon loader (#72) — resolves an ability's icon key to a bundled
 * image URL, entirely client-side (presentation layer).
 *
 * BOUNDARY: icons are cosmetic/presentation and MUST NOT touch the sim or the
 * content hash. The hashed `ABILITY_SCHEMA` (packages/content) carries no icon
 * field. Icon assignment lives here in the client, keyed by champion + slot,
 * exactly like the placeholder name table in `abilityLabels.ts`. A
 * config-authored champion gets icons by adding a row here (or, later, a
 * trimmed client manifest) — never by per-champion render code.
 *
 * ASSET RESOLUTION: the art lives in `packages/content/assets/icons/abilities/
 * <class>/<file>.png` (see that folder's README + #72 spec: 256x256,
 * transparent, art-only). Vite's `import.meta.glob` maps each file to a
 * content-hashed URL at build time. The client requests `abilityIconSrc(...)`;
 * a slot with no assigned/loadable icon returns `null`, and the ActionBar
 * falls back to its letter glyph — so this ships safely before any art exists.
 *
 * #695: both `.png` and `.avif` are accepted side by side for the same
 * extension-less key (`<class>/<file>`). AVIF compresses far better for this
 * asset class and every target browser supports it in `<img>`; this is a
 * loader-level change only — `ICON_KEYS_BY_CHAMPION` entries stay
 * extension-less so existing PNG rows are untouched. If a key somehow has
 * BOTH a `.png` and a `.avif` committed, `.avif` wins — deliberate, final
 * precedence (see `loadIconUrlMap`), not an open question.
 *
 * #322: shares `ACTION_SLOT_COUNT` (bumped 8 → 12) with `abilityLabels.ts` —
 * `EMPTY_ICON_KEYS` below mirrors that file's `EMPTY_LABELS` fallback shape,
 * even though a per-champion lookup miss already falls back to `null` on its
 * own (out-of-range indexing into `undefined` is safe here either way); kept
 * for the same reason `abilityLabels.ts` keeps its fallback array — a single,
 * explicit, right-sized "no data" shape instead of relying on `undefined`
 * indexing coincidentally doing the right thing.
 */

import { toChampionId } from "@pit/protocol";
import { ACTION_SLOT_COUNT } from "./abilityLabels.js";

/**
 * Fighter's (formerly Warrior's, #546 trial rename) champion id. Literal
 * opaque id, like the other table entries below (mirrors the
 * AzerothCore-precedent scoping decision: per-file local resolution, not a
 * shared "well-known defaults" module) — previously resolved via
 * `catalog.ts`'s `championBySlugOrThrow("warrior")`, which was slug-keyed
 * and so needed the same hand-edit on rename as a literal id would, just
 * with extra indirection (#546 removed that helper; see git history if a
 * genuine slug lookup is needed again, e.g. URL routing).
 */
// eslint-disable-next-line pit/no-hardcoded-champion-id
const WARRIOR_ID = toChampionId("chm_BJ4RLXqiBG"); // slug: fighter (formerly warrior, #546)

/**
 * The other five playable champions' ids, same literal-opaque-id convention as
 * `WARRIOR_ID` above (per-file local resolution, not a shared defaults module).
 * `dummy` and `reflecttest` are deliberately absent — they're test/target
 * fixtures, not playable kits, and get no art.
 */
// eslint-disable-next-line pit/no-hardcoded-champion-id
const AFFLICTOR_ID = toChampionId("chm_GPNhe2YP3C"); // slug: afflictor
// eslint-disable-next-line pit/no-hardcoded-champion-id
const HUNTER_ID = toChampionId("chm_DcCpRLeXNm"); // slug: hunter
// eslint-disable-next-line pit/no-hardcoded-champion-id
const MAGE_ID = toChampionId("chm_0TAZFt86RV"); // slug: mage
// eslint-disable-next-line pit/no-hardcoded-champion-id
const PRIEST_ID = toChampionId("chm_OoXhilEgJi"); // slug: priest
// eslint-disable-next-line pit/no-hardcoded-champion-id
const ROGUE_ID = toChampionId("chm_EQeduxkZ7r"); // slug: rogue

/**
 * Icon KEYS per champion, slot-indexed (index i = ability slot i). A `null`
 * entry means "no icon assigned" -> letter-glyph fallback. The key is the path
 * under `abilities/`, WITHOUT extension: `"<class>/<file>"`.
 *
 * Intentionally separate from the name table in `abilityLabels.ts` (names are
 * text; icons are art) but mirrors its shape. Populate as art lands.
 */
const ICON_KEYS_BY_CHAMPION: Readonly<Record<string, readonly (string | null)[]>> = {
  // #163: keyed by opaque champion id (trailing comment = human slug).
  // Fighter (formerly Warrior, #546): #311 FULL REBUILD (supersedes #78's
  // kit), #322 bumped slot count 8 -> 12. 12-slot kit: Rend(0)/Hamstring(1)/
  // Piercing Howl(2)/Intimidating Shout(3)/Pommel Strike(4)/Spell Reflect(5)/
  // Stand Behind Me!(6)/Cleave(7)/Charge(8)/Battleshout(9)/Defensive Strike(10)/
  // Execute(11) — see abilityLabels.test.ts for the authoritative slot order.
  // The old slot 0 art (`strike.png`, #171's smoke-test icon, now living at
  // `fighter/strike.png` after the folder rename) no longer matches ANY
  // ability in the rebuilt kit (slot 0 is Rend, not Strike) so it stays
  // unassigned here (orphaned dead art from a superseded kit design, not a
  // player-visible placeholder — no slot maps to it, so it never renders).
  // Slot 8 (Charge) has art: `fighter/charge.png` (uploaded directly, not
  // via any committed tool — see git history on that file if the source
  // matters).
  //
  // #696/#671 (2026-07-24): generation is Recraft-DIRECT (generate ->
  // removeBackground, both plain REST, key in 1Password as `recraft api
  // key`), NOT `tools/art-gen`'s fal.ai pipeline. Joseph dropped fal for
  // icons entirely after #671 found fal-via-Recraft's prompt template
  // rendering literal roses/feathers instead of reading "rose"/"feathered"
  // as color/lighting adjectives — confirmed reproducible, not a one-off
  // seed. Fal.ai itself isn't banned project-wide (still fine for
  // `tools/art-gen`'s arena/character-concept output per that tool's own
  // scope), just never for ability icons.
  //
  // 2026-07-26: every slot below now has real Recraft-direct art, generated
  // from hand-authored per-ability "pose-brief" prompts (the visual-brief
  // translation #696 identified as the missing step — authored by hand, so
  // the `describeAbilityForArt` automation it proposed stayed unbuilt and
  // #749 was closed as unnecessary). Full procedure, prompt template, and
  // the type -> glow-hue table live in
  // `docs/runbooks/generate-ability-icon.md`.
  //
  // SLOT CEILING: `ACTION_SLOT_COUNT` is 12, but afflictor/fighter author 13
  // abilities and hunter/mage author 15. Art for those trailing abilities IS
  // committed (see each folder) but is NOT wired here — there is no slot to
  // wire it to. Those rows stay exactly 12 long; raising the ceiling is out
  // of scope for an art PR and would need its own issue (HUD layout, keybind
  // defaults, and `abilityLabels.ts` all key off the same constant).
  [WARRIOR_ID]: [
    "fighter/rend", // 0: Rend
    "fighter/hamstring", // 1: Hamstring
    "fighter/piercing-howl", // 2: Piercing Howl
    "fighter/intimidating-shout", // 3: Intimidating Shout
    "fighter/pommel-strike", // 4: Pommel Strike
    "fighter/spell-reflect-warrior", // 5: Spell Reflect
    "fighter/stand-behind-me", // 6: Stand Behind Me!
    "fighter/cleave", // 7: Cleave
    "fighter/charge", // 8: Charge
    "fighter/battleshout", // 9: Battleshout
    "fighter/defensive-strike", // 10: Defensive Strike
    "fighter/execute", // 11: Execute
    // slot 12 (Stay Mad) art committed at `fighter/stay-mad.png`, unwired —
    // beyond ACTION_SLOT_COUNT.
  ], // slug: fighter (formerly warrior, #546)
  [AFFLICTOR_ID]: [
    "afflictor/corruption", // 0: Corruption
    "afflictor/unstable-affliction", // 1: Unstable Affliction
    "afflictor/shadow-bolt", // 2: Shadow Bolt
    "afflictor/curse-of-exhaustion", // 3: Curse of Exhaustion
    "afflictor/fear", // 4: Fear
    "afflictor/shadowfury", // 5: Shadowfury
    "afflictor/demonic-circle", // 6: Demonic Circle
    "afflictor/healthstone", // 7: Healthstone
    "afflictor/interrupt", // 8: Interrupt
    "afflictor/death-coil", // 9: Death Coil
    "afflictor/destruction", // 10: Destruction
    "afflictor/drain-life", // 11: Drain Life
    // slot 12 (Chaos Void) art committed at `afflictor/chaos-void.png`,
    // unwired — beyond ACTION_SLOT_COUNT.
  ], // slug: afflictor
  [HUNTER_ID]: [
    "hunter/poison-dart", // 0: Poison Dart
    "hunter/disengage", // 1: Disengage
    "hunter/wing-clip", // 2: Wing Clip
    "hunter/steady-shot", // 3: Steady Shot
    "hunter/bola-throw", // 4: Bola Throw
    "hunter/rain-of-arrows", // 5: Rain of Arrows
    "hunter/serpent-arrow", // 6: Serpent Arrow
    "hunter/move-quick", // 7: Move Quick
    "hunter/bandage", // 8: Bandage
    "hunter/silencing-shot", // 9: Silencing Shot
    "hunter/burst-fire", // 10: Burst Fire
    "hunter/harpoon", // 11: Harpoon
    // slots 12-14 (Aimed Shot, Shotgun, Bullet Time) art committed in
    // `hunter/`, unwired — beyond ACTION_SLOT_COUNT.
  ], // slug: hunter
  [MAGE_ID]: [
    "mage/frostbolt", // 0: Frostbolt
    "mage/fireball", // 1: Fireball
    "mage/meteor", // 2: Meteor
    "mage/frost-nova", // 3: Frost Nova
    "mage/cone-of-cold", // 4: Cone of Cold
    "mage/shimmer", // 5: Shimmer
    "mage/ice-barrier", // 6: Ice Barrier
    "mage/counterspell", // 7: Counterspell
    "mage/ice-lance", // 8: Ice Lance
    "mage/polymorph", // 9: Polymorph
    "mage/ice-block", // 10: Ice Block
    "mage/blink", // 11: Blink
    // slots 12-14 (Ice Wall, Frost Geyser, Fireblast) art committed in
    // `mage/`, unwired — beyond ACTION_SLOT_COUNT.
  ], // slug: mage
  [PRIEST_ID]: [
    "priest/renew", // 0: Renew
    "priest/power-word-shield", // 1: Power Word: Shield
    "priest/miracle", // 2: Miracle
    "priest/psychic-scream", // 3: Psychic Scream
    "priest/shadow-word-pain", // 4: Shadow Word: Pain
    "priest/holy-orb", // 5: Holy Orb
    "priest/protect-the-weak", // 6: Protect the Weak
    "priest/leap-of-faith", // 7: Leap of Faith
    "priest/heal", // 8: Heal
    "priest/seek-cover", // 9: Seek Cover
    "priest/smite", // 10: Smite
    null, // 11: unauthored (priest kit has 11 abilities)
  ], // slug: priest
  // #759 unequipped Stealth and backfilled Vanish into its slot 1 WITHOUT
  // realigning this row (it still named `rogue/stealth` at 1 and pushed every
  // later ability one slot late). Realigned here as part of #760, which appends
  // three more abilities and so would otherwise have inherited the skew.
  [ROGUE_ID]: [
    "rogue/shadowstep", // 0: Shadowstep
    "rogue/vanish", // 1: Vanish (took Stealth's freed slot, #759)
    "rogue/backstab", // 2: Backstab
    "rogue/rupture", // 3: Rupture
    "rogue/fan-of-knives", // 4: Fan of Knives
    "rogue/eviscerate", // 5: Eviscerate
    "rogue/kidney-shot", // 6: Kidney Shot
    "rogue/phase", // 7: Phase
    "rogue/dark-strike", // 8: Dark Strike
    null, // 9: Smoke Bomb — art deferred (#760)
    null, // 10: Dagger Throw — art deferred (#760)
    null, // 11: Well Prepared — art deferred (#760)
    // `rogue/stealth.png` is committed but unwired — Stealth is authored and
    // equipped by nobody since #759, so no slot maps to it.
  ], // slug: rogue
};

/** #322: fallback fixed-length "no icons" row for a champion with no row at
 *  all — mirrors `abilityLabels.ts`'s `EMPTY_LABELS`. Sized to
 *  `ACTION_SLOT_COUNT` (12). */
const EMPTY_ICON_KEYS: readonly (string | null)[] = Array.from(
  { length: ACTION_SLOT_COUNT },
  () => null,
);

/**
 * Build-time map of every ability icon (PNG or AVIF) -> its bundled URL.
 * Eager + `?url` so each entry is a ready string. Glob base is the content
 * assets folder (outside the client package; Vite `server.fs.allow` includes
 * the repo root). Indexed by the trailing `<class>/<file>` key.
 *
 * #695: PNG and AVIF are two separate globs, merged into one map by inserting
 * PNG entries first and then AVIF entries — a later `Map.set` for the same
 * key overwrites the earlier one, so when both extensions exist for a key the
 * `.avif` URL wins. This is the deliberate, final precedence rule (prefer
 * AVIF, fall back to PNG), not an artifact of glob/filesystem ordering.
 *
 * Vitest runs on Vite's transform pipeline, so this resolves for real in tests
 * too (a committed PNG/AVIF gets a real bundled URL there as well); a
 * genuinely non-Vite/non-Vitest environment would leave `import.meta.glob`
 * undefined -> empty map -> every lookup returns null -> letter-glyph
 * fallback either way.
 */
function loadIconUrlMap(): ReadonlyMap<string, string> {
  // `import.meta.glob(...)` MUST stay a literal call — Vite only transforms the
  // literal form; a cast/aliased `import.meta.glob` is left undefined at build
  // so assets never bundle (see #181).
  const pngModules = import.meta.glob<string>(
    "../../../../packages/content/assets/icons/abilities/**/*.png",
    { eager: true, query: "?url", import: "default" },
  );
  const avifModules = import.meta.glob<string>(
    "../../../../packages/content/assets/icons/abilities/**/*.avif",
    { eager: true, query: "?url", import: "default" },
  );
  return mergeIconUrlModules(pngModules, avifModules);
}

/**
 * Pure merge of glob module maps (path -> url) for the two accepted
 * extensions into one key -> url map. Exported (test-only, #695) so the
 * `.avif`-over-`.png` precedence rule can be pinned directly against
 * synthetic path/url fixtures, without depending on which extensions happen
 * to be committed under `assets/icons/abilities/` at test time.
 */
export function mergeIconUrlModules(
  pngModules: Readonly<Record<string, string>>,
  avifModules: Readonly<Record<string, string>>,
): ReadonlyMap<string, string> {
  const map = new Map<string, string>();
  // PNG first, then AVIF — AVIF entries overwrite PNG entries for the same
  // key (precedence: prefer .avif when both are committed for a key, #695).
  for (const [path, url] of Object.entries(pngModules)) {
    const key = iconKeyFromPath(path);
    if (key !== null) map.set(key, url);
  }
  for (const [path, url] of Object.entries(avifModules)) {
    const key = iconKeyFromPath(path);
    if (key !== null) map.set(key, url);
  }
  return map;
}

/**
 * Extract the `<class>/<file>` key (no extension) from a glob module path.
 * e.g. ".../assets/icons/abilities/warrior/strike.png" -> "warrior/strike",
 * ".../assets/icons/abilities/fighter/charge.avif" -> "fighter/charge" (#695).
 * Returns null if the path doesn't sit under an `abilities/` segment.
 *
 * Exported (test-only, #695) so extension-stripping can be pinned directly.
 */
export function iconKeyFromPath(path: string): string | null {
  const marker = "/abilities/";
  const idx = path.lastIndexOf(marker);
  if (idx === -1) return null;
  const tail = path.slice(idx + marker.length);
  return tail.replace(/\.(?:png|avif)$/u, "");
}

const ICON_URL_BY_KEY: ReadonlyMap<string, string> = loadIconUrlMap();

/**
 * Resolve the bundled image URL for `championId`'s ability in `slot`, or
 * `null` if the slot has no assigned icon OR the asset isn't present. Pure
 * lookup; callers (ActionBar) fall back to the letter glyph on null.
 *
 * #76: NOT yet resolved through `resolveDisplayAbilityMeta` — this table is
 * hand-maintained and keyed by champion+SLOT only (no art exists for ANY
 * ability yet, Ambush included, so a swap here has zero observable effect
 * today). `abilityLabels.ts`'s `abilityLabelResolved`/`abilityIconLetterResolved`
 * ARE wired (the observable, text-based half of the #76 cosmetic swap). When
 * real icon art lands for a conditional-slot pair, this function's key table
 * needs a per-ABILITY-id (not just per-slot) entry and a resolved variant
 * mirroring `abilityLabelResolved` — tracked here rather than speculatively
 * built now with nothing to point at.
 */
/** Resolve a canonical ability icon directly by shared class/file key. */
export function abilityIconSrcForKey(key: string): string | null {
  return ICON_URL_BY_KEY.get(key) ?? null;
}

export function abilityIconSrc(championId: string, slot: number): string | null {
  const keys = ICON_KEYS_BY_CHAMPION[championId] ?? EMPTY_ICON_KEYS;
  const key = keys[slot] ?? null;
  if (key === null) return null;
  return ICON_URL_BY_KEY.get(key) ?? null;
}

/** Test/introspection helper: the icon key assigned to a slot, or null. */
export function abilityIconKey(championId: string, slot: number): string | null {
  const keys = ICON_KEYS_BY_CHAMPION[championId] ?? EMPTY_ICON_KEYS;
  return keys[slot] ?? null;
}
